import { Checkbox, type CheckedState } from "@/components/flexkit/checkbox/Checkbox";
import { Label } from "@/components/podkit/forms/Label";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

type EnvironmentCreationRoleProps = {
    editable?: boolean;
};

export const EnvironmentCreationRole: FC<EnvironmentCreationRoleProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [isChecked, setIsChecked] = useState<CheckedState>(false);
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    useEffect(() => {
        if (organizationPolicies) {
            const requireProjects = organizationPolicies.membersRequireProjects || false;
            const createProjects = organizationPolicies.membersCreateProjects || false;

            // If the policy values are in an unexpected state (both true or both false),
            // set checkbox to indeterminate state
            if (requireProjects === createProjects) {
                setIsChecked("indeterminate");
            } else {
                // When membersRequireProjects is true and membersCreateProjects is false, checkbox should be checked
                setIsChecked(requireProjects && !createProjects);
            }
        }
    }, [organizationPolicies]);

    const handleChange = useCallback(
        async (checked: CheckedState) => {
            const prevState = isChecked;
            // If checkbox is in indeterminate state and user clicks,
            // we treat it as moving to checked state
            const newChecked = checked === "indeterminate" ? true : checked;
            setIsChecked(newChecked);

            const params: PartialMessage<OrganizationPolicies> = {
                // When checked: membersRequireProjects = true, membersCreateProjects = false
                // When unchecked: membersRequireProjects = false, membersCreateProjects = true
                membersRequireProjects: newChecked === true,
                membersCreateProjects: newChecked === false,
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: "Environment creation policy updated",
                });
            } catch (error) {
                console.error(error);
                // Revert UI state on error
                setIsChecked(() => prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [isChecked, updateOrganizationPolicies, toast],
    );

    return (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <Label className="flex items-center gap-2">
                            <Checkbox
                                data-testid="environment-creation-role-checkbox"
                                id="members-require-projects"
                                checked={isChecked}
                                onCheckedChange={handleChange}
                                disabled={!editable}
                            />
                            <span className="text-base font-medium">Environments can only be created from projects</span>
                        </Label>
                    </div>
                    <Text className="pl-6 text-sm text-content-secondary">
                        This only affects the users with member role.{" "}
                        <ExternalLink
                            href="https://ona.com/docs/ona/organizations/policies#environment-creation-restrictions"
                            iconSize="sm"
                        >
                            Learn more
                        </ExternalLink>
                    </Text>
                </div>
            </SkeletonBlock>
        </div>
    );
};
